/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import { HOME_HREF, urlPartPositions } from '../constants';

/**
 * This functions takes an url and returns a site object,
 * an error will be thrown if no url is passed in or no site is found
 * @param {string} url
 * @returns {object} site - a site object
 */
export const resolveSiteFromUrl = (url) => {
    if (!url) {
        throw new Error('URL is required to find a site object.');
    }
    const { pathname, search } = new URL(absoluteUrl(url));
    const path = `${pathname}${search}`;

    // get the site identifier from the url
    const { siteRef } = getParamsFromPath(path);
    const sites = getSites();

    // step 1: use the siteRef to look for the site from the sites in the app config
    // since alias is optional, make sure it is defined before the equality check
    const siteFound = sites.find(
        (site) => site.id === siteRef || (site.alias && site.alias === siteRef),
    );
    if (siteFound) {
        return siteFound;
    }

    // Step 2: if step 1 does not work, use the defaultSite value to get the default site
    const defaultSite = getDefaultSite();
    // Step 3: throw an error if site can't be found by any of the above steps
    if (!defaultSite) {
        throw new Error(
            "Can't find a matching default site. Please check your sites configuration.",
        );
    }
    return defaultSite;
};

/**
 * Returns the default site based on the defaultSite value from the app config
 * @returns {import('@salesforce/pwa-kit-runtime/utils/ssr-config').Site} site - a site object from app config
 */
export const getDefaultSite = () => {
    const { app } = getConfig();
    const sites = getSites();

    if (sites.length === 1) {
        return sites[0];
    }

    return sites.find((site) => site.id === app.defaultSite);
};

/**
 * Return the list of sites that has included their respective aliases
 * @return {import('@salesforce/pwa-kit-runtime/utils/ssr-config').Site[]} sites - list of sites including their aliases
 */
export const getSites = () => {
    const { sites = [], siteAliases = {} } = getConfig().app || {};

    if (!sites.length) {
        throw new Error("Can't find any sites from the config. Please check your configuration");
    }

    return sites.map((site) => {
        const alias = siteAliases[site.id];
        return {
            ...site,
            ...(alias ? { alias } : {}),
        };
    });
};

/**
 * Given a site reference, return the site object
 * @param {string} siteRef - site reference to look for the site object
 * @returns {import('@salesforce/pwa-kit-runtime/utils/ssr-config').Site | undefined} found site object or default site object
 */
export const getSiteByReference = (siteRef) => {
    const defaultSite = getDefaultSite();
    const sites = getSites();

    return sites.find((site) => site.alias === siteRef || site.id === siteRef) || defaultSite;
};

/**
 * A function that takes a path and qualifies it with the current host and protocol.
 * This function works on the client and on the server.
 *
 * @example
 * absoluteUrl(/women/dresses?color=black)
 *
 * // => http(s)://www.site.com/women/dresses?color=black
 * @param path
 * @returns {string|*}
 */
export const absoluteUrl = (path) => new URL(path, getAppOrigin()).toString();

/**
 * Modifies a given url by adding/updating query parameters.
 *
 * @param {string} url - The base url of the output url set.
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string} A URL with additional params
 * @example
 * import {rebuildPathWithParams} from '/path/to/utils/url'
 *
 * rebuildPathWithParams(
 *     '/en-GB/product/25501032M',
 *     {color: 'JJ2SKXX', size: 'MD'}
 * )
 *
 * // Returns
 * // '/en-GB/product/25501032M?color=JJ2SKXX&size=9MD'
 */
export const rebuildPathWithParams = (url, extraParams) => {
    const [pathname, search] = url.split('?');
    const params = new URLSearchParams(search);

    // Apply any extra params.
    Object.keys(extraParams).forEach((key) => {
        const value = extraParams[key];

        // 0 is a valid value as for a param
        if (!value && value !== 0) {
            params.delete(key);
        } else {
            params.set(key, value);
        }
    });

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');

    // Generate the newly updated url.
    return `${pathname}${Array.from(paramStr).length > 0 ? `?${paramStr}` : ''}`;
};

/**
 * Builds a list of modified Urls with the provided params key and values,
 * preserving any search params provided in the original url.Optionally
 * you can pass and object used to set static params values.
 * @param {string} url - The base url of the output url set.
 * @param {string} key - The search params for the associated values
 * @param {Array} values - The search param values
 * @param {object} extraParams - A key values pairing used to add static search param values.
 * @returns {string[]} A list of URLs
 * @example
 * import {buildUrlSet} from '/path/to/utils/url'
 *
 * buildUrlSet(
 *     '/womens/clothing',
 *     'sort',
 *     ['price-high-to-low', 'price-low-to-high'],
 *     {offset: 0}
 * )
 *
 * // Returns
 * // ['/womens/clothing?sort=price-high-to-low', '/womens/clothing?sort=price-low-to-high']
 */
export const buildUrlSet = (url = '', key = '', values = [], extraParams = {}) =>
    values.map((value) => rebuildPathWithParams(url, { [key]: value, ...extraParams }));

/**
 * Given a category and the current locale returns an href to the product list page.
 *
 * @param {Object} category
 * @returns {string}
 */
export const categoryUrlBuilder = (category) => encodeURI(`/category/${category.id}`);

/**
 * Given a product and the current locale returns an href to the product detail page.
 *
 * @param {Object} product
 * @returns {string}
 */
export const productUrlBuilder = (product) => encodeURI(`/product/${product.id}`);

/**
 * Given a search term, constructs a search url.
 *
 * @param {string} searchTerm
 * @returns {string}
 */
export const searchUrlBuilder = (searchTerm) =>
    `/search?${new URLSearchParams({ q: searchTerm }).toString()}`;

/**
 * Returns a relative URL for a locale short code.
 * Based on your app configuration, this function will replace your current locale shortCode with a new one
 *
 * @param {String} shortCode - The locale short code.
 * @param {function(*, *, *, *=): string} - Generates a site URL from the provided path, site and locale.
 * @param {string[]} opts.disallowParams - URL parameters to remove
 * @param {Object} opts.location - location object to replace the default `window.location`
 * @returns {String} url - The relative URL for the specific locale.
 */
export const getPathWithLocale = (shortCode, buildUrl, opts = {}) => {
    const location = opts.location ? opts.location : window.location;
    const { siteRef, localeRef } = getParamsFromPath(`${location.pathname}${location.search}`);
    let { pathname, search } = location;

    // sanitize the site from current url if existing
    if (siteRef) {
        pathname = pathname.replace(`/${siteRef}`, '');
        search = search.replace(`site=${siteRef}`, '');
    }
    // sanitize the locale from current url if existing
    if (localeRef) {
        pathname = pathname.replace(`/${localeRef}`, '');
        search = search.replace(`locale=${localeRef}`, '');
    }
    // remove ending any &
    search = search.replace(/&$/, '');

    const defaultSite = getDefaultSite();

    // Remove query parameters
    const { disallowParams = [] } = opts;

    const queryString = new URLSearchParams(`${search}`);

    if (disallowParams.length) {
        disallowParams.forEach((param) => {
            queryString.delete(param);
        });
    }

    const site = getSiteByReference(siteRef);

    const locale = getLocaleByReference(site, shortCode);

    // rebuild the url with new locale,
    const newUrl = buildUrl(
        `${pathname}${Array.from(queryString).length !== 0 ? `?${queryString}` : ''}`,
        // By default, as for home page, when the values of site and locale belongs to the default site,
        // they will be not shown in the url just
        defaultSite.alias || defaultSite.id,
        locale?.alias || locale?.id,
    );
    return newUrl;
};

/**
 * Generates the URL Template literal (Template string) used to build URLs in the App according
 * the current selected site/locale and the default App URL configuration.
 *
 * @param {import('@salesforce/pwa-kit-runtime/utils/ssr-config').App} appConfig Application default configuration.
 * @param {string} siteRef Current selected Site reference. The value can be the Site id or alias.
 * @param {string} localeRef Current selected Locale reference. The value can be the Locale id or alias.
 */
export const createUrlTemplate = (appConfig, siteRef, localeRef) => {
    const {
        site: siteConfig,
        locale: localeConfig,
        showDefaults: showDefaultsConfig,
    } = appConfig.url;
    const defaultSite = getDefaultSite();
    const site = getSiteByReference(siteRef);
    const defaultLocale = getLocaleByReference(site, site.l10n.defaultLocale);

    const isDefaultSite =
        defaultSite.id === siteRef || (defaultSite.alias && defaultSite.alias === siteRef);
    const isDefaultLocale =
        defaultLocale.id === localeRef ||
        (defaultLocale.alias && defaultLocale.alias === localeRef);

    const querySite =
        (siteConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultSite);
    const queryLocale =
        (localeConfig === urlPartPositions.QUERY_PARAM && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.QUERY_PARAM && !showDefaultsConfig && !isDefaultLocale);

    const isQuery = querySite || queryLocale;

    const pathSite =
        (siteConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (siteConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultSite);
    const pathLocale =
        (localeConfig === urlPartPositions.PATH && showDefaultsConfig) ||
        (localeConfig === urlPartPositions.PATH && !showDefaultsConfig && !isDefaultLocale);

    /**
     * function providing: path, site and locale generates a URL.
     * @param {string} path
     * @param {string} [website]
     * @param {string} [locale]
     * @returns {string} Url
     */
    return (path, website, locale) => {
        const isHomeWithDefaultSiteAndLocale =
            path === HOME_HREF &&
            (defaultSite.id === website || (defaultSite.alias && defaultSite.alias === website)) &&
            (defaultLocale.id === locale ||
                (defaultLocale.alias && defaultLocale.alias === locale));

        const sitePath =
            pathSite && website && !isHomeWithDefaultSiteAndLocale ? `/${website}` : '';
        const localePath =
            pathLocale && locale && !isHomeWithDefaultSiteAndLocale ? `/${locale}` : '';

        const hasQuery = isQuery && (website || locale) && !isHomeWithDefaultSiteAndLocale;
        let queryString = '';
        if (hasQuery) {
            const searchParams = new URLSearchParams();
            if (querySite && website) {
                searchParams.append('site', website);
            }
            if (queryLocale && locale) {
                searchParams.append('locale', locale);
            }
            queryString = `?${searchParams.toString()}`;
        }
        return `${sitePath}${localePath}${path}${queryString}`;
    };
};

/*
 * Remove query params from a give url path based on a given list of keys
 *
 * @param {string} path - The part of url to have params removed from.
 * @param {array} keys - list of params to be removed
 * @returns {string} - the url after param has been removed
 * @example
 * import {removeQueryParamsFromPath} from /path/to/util/url
 *
 * removeQueryParamsFromPath(
 *   /en-GB/cart?pid=1234&color=black&size=s&abc=12,
 *   ['pid', 'color', 'size']
 * )
 * // returns
 * // '/en-GB/cart?abc=12'
 */
export const removeQueryParamsFromPath = (path, keys) => {
    const [pathname, search] = path.split('?');
    const params = new URLSearchParams(search);
    keys.forEach((key) => {
        if (params.has(key)) {
            params.delete(key);
        }
    });

    // Clean up any trailing `=` for params without values.
    const paramStr = params.toString().replace(/=&/g, '&').replace(/=$/, '');

    return `${pathname}${paramStr && '?'}${paramStr}`;
};

/**
 * Given a site and a locale reference, return the locale object
 * @param site - site to look for the locale
 * @param localeRef - the locale ref to look for in site supported locales
 * @return {object|undefined}
 */
export const getLocaleByReference = (site, localeRef) => {
    if (!site) {
        throw new Error('Site is not defined. It is required to look for locale object');
    }
    return site.l10n.supportedLocales.find(
        (locale) => locale.id === localeRef || locale.alias === localeRef,
    );
};

/**
 * Given your application's configuration this function returns a set of regular expressions used to match the site
 * and locale references from an url.
 * @param config
 * @return {{searchMatcherForSite: RegExp, searchMatcherForLocale: RegExp, pathMatcher: RegExp}}
 */
export const getConfigMatcher = (config) => {
    if (!config) {
        throw new Error('Config is not defined.');
    }

    const allSites = getSites();
    const siteIds = [];
    const siteAliases = [];
    const localesIds = [];
    const localeAliases = [];
    allSites.forEach((site) => {
        siteAliases.push(site.alias);
        siteIds.push(site.id);
        const { l10n } = site;
        l10n.supportedLocales.forEach((locale) => {
            localesIds.push(locale.id);
            localeAliases.push(locale.alias);
        });
    });
    const sites = [...siteIds, ...siteAliases].filter(Boolean);
    const locales = [...localesIds, ...localeAliases].filter(Boolean);

    // prettier-ignore
    const searchPatternForSite = `site=(?<site>${sites.join('|')})`
    // prettier-ignore
    // eslint-disable-next-line no-useless-escape
    const pathPattern = `(?:\/(?<site>${sites.join('|')}))?(?:\/(?<locale>${locales.join("|")}))?(?!\\w)`
    // prettier-ignore
    const searchPatternForLocale = `locale=(?<locale>${locales.join('|')})`
    const pathMatcher = new RegExp(pathPattern);
    const searchMatcherForSite = new RegExp(searchPatternForSite);
    const searchMatcherForLocale = new RegExp(searchPatternForLocale);
    return {
        pathMatcher,
        searchMatcherForSite,
        searchMatcherForLocale,
    };
};

/**
 * This function return the identifiers (site and locale) from the given url
 * The site will always go before locale if both of them are presented in the pathname
 * @param path {string}
 * @returns {{siteRef: string, localeRef: string}} - site and locale reference (it could either be id or alias)
 */
export const getParamsFromPath = (path) => {
    const { pathname, search } = new URL(absoluteUrl(path));

    const config = getConfig();
    const { pathMatcher, searchMatcherForSite, searchMatcherForLocale } = getConfigMatcher(config);
    const pathMatch = pathname.match(pathMatcher);
    const searchMatchForSite = search.match(searchMatcherForSite);
    const searchMatchForLocale = search.match(searchMatcherForLocale);

    // the value can only either in the path or search query param, there will be no overridden
    const siteRef = pathMatch?.groups.site || searchMatchForSite?.groups.site;

    const localeRef = pathMatch?.groups.locale || searchMatchForLocale?.groups.locale;
    return { siteRef, localeRef };
};

export const isLowEnv = () =>
    (typeof window === 'undefined' &&
        process.env.DEPLOY_TARGET !== 'production' &&
        process.env.DEPLOY_TARGET !== 'staging') ||
    (typeof window !== 'undefined' &&
        window.__CONFIG__.deployTarget &&
        window.__CONFIG__.deployTarget !== 'production' &&
        window.__CONFIG__.deployTarget !== 'staging');
